import { Link } from "react-router-dom";
import { Phone, Clock, Mail, MapPin, Printer, Flame, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { company, services } from "../mock";

const socialLinks = [
  { icon: Facebook, href: company.social.facebook, label: "Facebook" },
  { icon: Instagram, href: company.social.instagram, label: "Instagram" },
  { icon: Twitter, href: company.social.twitter, label: "Twitter" },
  { icon: Youtube, href: company.social.youtube, label: "Youtube" },
];

const companyLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/gallery", label: "Gallery" },
  { to: "/services", label: "All Services" },
  { to: "/contact", label: "Contact" },
];

function BrandColumn() {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <span className="flex items-center justify-center w-10 h-10 rounded-md bg-gradient-to-br from-orange-500 to-amber-400">
          <Flame className="w-5 h-5 text-zinc-950" fill="currentColor" />
        </span>
        <span className="leading-tight">
          <span className="block font-display font-extrabold text-white text-lg">PHOENIX</span>
          <span className="block text-[10px] tracking-[0.2em] text-orange-400">DECORATIVE SURFACES</span>
          <span className="block text-[9px] tracking-[0.15em] text-zinc-500">WESTERN NEW YORK DIVISION</span>
        </span>
      </div>
      <p className="text-zinc-400 text-sm leading-relaxed">
        Over {company.yearsExperience} years turning concrete surfaces into durable works of art across Western New York.
      </p>
      <div className="flex items-center gap-2 mt-5">
        {socialLinks.map((s) => (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noreferrer"
            aria-label={s.label}
            className="w-9 h-9 flex items-center justify-center rounded-md bg-zinc-900 text-zinc-400 hover:bg-orange-500 hover:text-white transition-colors"
          >
            <s.icon className="w-4 h-4" />
          </a>
        ))}
      </div>
    </div>
  );
}

function LinkColumn({ title, children }) {
  return (
    <div>
      <h4 className="text-white font-semibold mb-4 text-sm tracking-wide uppercase">{title}</h4>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}

function ContactColumn() {
  const items = [
    { id: "phone", icon: Phone, content: <a href={`tel:${company.phone}`} className="hover:text-orange-400">{company.phone}</a> },
    { id: "fax", icon: Printer, content: `Fax: ${company.fax}` },
    { id: "email", icon: Mail, content: <a href={`mailto:${company.email}`} className="hover:text-orange-400">{company.email}</a> },
    { id: "hours", icon: Clock, content: company.hours },
    { id: "address", icon: MapPin, content: company.address },
  ];
  return (
    <div>
      <h4 className="text-white font-semibold mb-4 text-sm tracking-wide uppercase">Get in Touch</h4>
      <ul className="space-y-3 text-sm">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-3 text-zinc-400">
            <item.icon className="w-4 h-4 text-orange-500 mt-0.5" /> {item.content}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-zinc-950 border-t border-zinc-800/60 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <BrandColumn />

          <LinkColumn title="Services">
            {services.slice(0, 6).map((s) => (
              <li key={s.id}>
                <Link to="/services" className="text-zinc-400 text-sm hover:text-orange-400 transition-colors">
                  {s.title}
                </Link>
              </li>
            ))}
          </LinkColumn>

          <LinkColumn title="Company">
            {companyLinks.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-zinc-400 text-sm hover:text-orange-400 transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </LinkColumn>

          <ContactColumn />
        </div>

        <div className="border-t border-zinc-800/60 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-zinc-500 text-sm">
            © {new Date().getFullYear()} {company.name}. All rights reserved.
          </p>
          <p className="text-zinc-600 text-xs">Epoxy Flooring · Concrete Polishing · Decorative Surfaces</p>
        </div>
      </div>
    </footer>
  );
}
